import api from './api';

export interface Ensayo {
  id: number;
  analito: string;
  medio: number;
  tipo: string;
  unidad: string;
  estado: number;
  // Campos extendidos
  regulado_id?: number | null;
  id_marco?: number | null;
  marco_nombre?: string | null;
  valor_min?: number | null;
  valor_max?: number | null;
  valor_ref?: number | null;
  presencia?: string | null;
  controlado_id?: number | null;
  id_organismo?: number | null;
  organismo_nombre?: string | null;
  metodo_deteccion?: string | null;
  limite_deteccion?: number | null;
}

export interface EnsayoRegulado {
  id: number;
  id_ensayo: number;
  id_marco: number;
  valor_min: number;
  valor_max: number;
  valor_ref: number;
  presencia: string;
  uso: number;
  analito?: string;
  unidad?: string;
  medio?: number;
  marco_nombre?: string | null;
}

export interface EnsayoControlado {
  id: number;
  id_ensayo: number;
  id_organismo: number;
  metodo_deteccion: string;
  limite_deteccion: number;
  analito?: string;
  unidad?: string;
  medio?: number;
  organismo_nombre?: string | null;
}

export interface EnsayoGestion extends Ensayo {
  medio_nombre?: string | null;
  regulados: EnsayoRegulado[];
  controlados: EnsayoControlado[];
}

// Obtener todos los ensayos
export const getEnsayos = async (): Promise<Ensayo[]> => {
  try {
    const response = await api.get('/ensayos');
    return response.data;
  } catch (error) {
    console.error('Error al obtener ensayos:', error);
    return [];
  }
};

export const getEnsayosGestion = async (): Promise<EnsayoGestion[]> => {
  const response = await api.get('/ensayos/gestion');
  return response.data;
};

// Obtener un ensayo por ID
export const getEnsayoById = async (id: number): Promise<Ensayo | null> => {
  try {
    const response = await api.get(`/ensayos/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener ensayo con ID ${id}:`, error);
    return null;
  }
};

// Crear un nuevo ensayo
export const createEnsayo = async (ensayo: Partial<Ensayo> & { analito: string; medio: number; tipo: string; unidad: string; estado?: number; regulado?: any; controlado?: any; }): Promise<Ensayo> => {
  const response = await api.post('/ensayos', ensayo);
  return response.data;
};

// Actualizar un ensayo
export const updateEnsayo = async (id: number, ensayo: Partial<Ensayo> & { regulado?: any; controlado?: any; remove_regulado?: boolean; remove_controlado?: boolean }): Promise<Ensayo> => {
  const response = await api.put(`/ensayos/${id}`, ensayo);
  return response.data;
};

// Eliminar un ensayo
export const deleteEnsayo = async (id: number): Promise<void> => {
  await api.delete(`/ensayos/${id}`);
};

// Buscar ensayos por criterios
export const searchEnsayos = async (searchTerm: string): Promise<Ensayo[]> => {
  try {
    const response = await api.get('/ensayos/search', {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error) {
    console.error('Error al buscar ensayos:', error);
    return [];
  }
};

// Obtener medios disponibles
export const getMedios = async (): Promise<{ id: number; medio: string }[]> => {
  try {
    const response = await api.get('/ensayos/medios');
    return response.data;
  } catch (error) {
    console.error('Error al obtener medios:', error);
    return [];
  }
};

export interface MarcoRegulatorio { id: number; marco: string }
export const getMarcosRegulatorios = async (): Promise<MarcoRegulatorio[]> => {
  try {
    const response = await api.get('/marcos-regulatorios');
    return response.data;
  } catch (error) {
    console.error('Error al obtener marcos regulatorios:', error);
    return [];
  }
};

export const getEnsayosRegulados = async (): Promise<EnsayoRegulado[]> => {
  const response = await api.get('/ensayos-regulados');
  return response.data;
};

export const createEnsayoRegulado = async (data: Omit<EnsayoRegulado, 'id' | 'analito' | 'unidad' | 'medio' | 'marco_nombre'>): Promise<EnsayoRegulado> => {
  const response = await api.post('/ensayos-regulados', data);
  return response.data;
};

export const updateEnsayoRegulado = async (id: number, data: Partial<EnsayoRegulado>): Promise<EnsayoRegulado> => {
  const response = await api.put(`/ensayos-regulados/${id}`, data);
  return response.data;
};

export const deleteEnsayoRegulado = async (id: number): Promise<void> => {
  await api.delete(`/ensayos-regulados/${id}`);
};

export interface OrganismoControl { id: number; organismo: string }
export const getOrganismosControl = async (): Promise<OrganismoControl[]> => {
  const response = await api.get('/organismos-control');
  return response.data;
};
